import { normalQuantile } from "@/lib/stats/distributions";
import { invertMonotone, regularizedBeta } from "@/lib/stats/special";

/**
 * Two-sample mark–recapture (the Lincoln–Petersen method).
 *
 * M animals are caught, marked and released; later C animals are caught, of which R
 * carry a mark. The population size N is estimated from the marked fraction R ÷ C of the
 * second sample matching the marked fraction M ÷ N of the population.
 *
 * - Lincoln–Petersen: N = M × C ÷ R (undefined when R = 0).
 * - Chapman (1951): N = (M + 1)(C + 1) ÷ (R + 1) − 1, less biased and defined at R = 0.
 * - Variance of the Chapman estimate (Seber):
 *   (M + 1)(C + 1)(M − R)(C − R) ÷ ((R + 1)² (R + 2)).
 * - Variance of the Lincoln–Petersen estimate (delta method, binomial R):
 *   M² C (C − R) ÷ R³.
 */

export interface RecaptureInput {
  /** M: animals marked and released in the first sample. */
  marked: number;
  /** C: animals caught in the second sample. */
  caught: number;
  /** R: marked animals found in the second sample. */
  recaptured: number;
}

export type RecaptureProblem = "marked" | "caught" | "recaptured" | "exceeds";

const isCount = (value: number, minimum: number) =>
  Number.isInteger(value) && value >= minimum;

export function recaptureProblem(
  input: RecaptureInput
): RecaptureProblem | null {
  if (!isCount(input.marked, 1)) {
    return "marked";
  }
  if (!isCount(input.caught, 1)) {
    return "caught";
  }
  if (!isCount(input.recaptured, 0)) {
    return "recaptured";
  }
  return input.recaptured > Math.min(input.marked, input.caught)
    ? "exceeds"
    : null;
}

/** M × C ÷ R, or null when nothing was recaptured. */
export function lincolnPetersen(input: RecaptureInput): number | null {
  const { marked, caught, recaptured } = input;
  return recaptured === 0 ? null : (marked * caught) / recaptured;
}

export function chapman(input: RecaptureInput): number {
  const { marked, caught, recaptured } = input;
  return ((marked + 1) * (caught + 1)) / (recaptured + 1) - 1;
}

export function chapmanVariance(input: RecaptureInput): number {
  const { marked: m, caught: c, recaptured: r } = input;
  return ((m + 1) * (c + 1) * (m - r) * (c - r)) / ((r + 1) ** 2 * (r + 2));
}

export function petersenVariance(input: RecaptureInput): number | null {
  const { marked: m, caught: c, recaptured: r } = input;
  return r === 0 ? null : (m * m * c * (c - r)) / r ** 3;
}

export interface Interval {
  lower: number;
  /** Null when the data set no upper limit (no recaptures). */
  upper: number | null;
}

/** The fewest animals that must exist: every distinct animal seen in either sample. */
export function distinctSeen(input: RecaptureInput): number {
  return input.marked + input.caught - input.recaptured;
}

/** The two-sided normal critical value for a confidence level such as 0.95. */
export function criticalZ(level: number): number {
  return normalQuantile(1 - (1 - level) / 2);
}

/** Estimate ± z standard errors, with the lower limit never below `floor`. */
export function normalInterval(
  estimate: number,
  variance: number,
  level: number,
  floor: number
): Interval {
  const half = criticalZ(level) * Math.sqrt(variance);
  return { lower: Math.max(floor, estimate - half), upper: estimate + half };
}

/** The Clopper–Pearson limits for a binomial proportion R out of C. */
export function clopperPearson(
  successes: number,
  trials: number,
  level: number
): { lower: number; upper: number } {
  const alpha = 1 - level;
  const quantile = (a: number, b: number, p: number) =>
    invertMonotone((x) => regularizedBeta(x, a, b), p, 0, 1);
  const lower =
    successes === 0
      ? 0
      : quantile(successes, trials - successes + 1, alpha / 2);
  const upper =
    successes === trials
      ? 1
      : quantile(successes + 1, trials - successes, 1 - alpha / 2);
  return { lower, upper };
}

/**
 * The exact binomial interval: treat R as binomial(C, M ÷ N), take the Clopper–Pearson
 * limits for M ÷ N, and invert them to N. It is asymmetric, as the uncertainty is, and
 * has no upper limit when R = 0.
 */
export function exactInterval(input: RecaptureInput, level: number): Interval {
  const { marked, caught, recaptured } = input;
  const p = clopperPearson(recaptured, caught, level);
  return {
    lower: Math.max(distinctSeen(input), marked / p.upper),
    upper: p.lower === 0 ? null : marked / p.lower,
  };
}

export interface RecaptureSummary {
  petersen: number | null;
  chapman: number;
  chapmanVariance: number;
  chapmanSe: number;
  normal: Interval;
  exact: Interval;
  seen: number;
  /** R ÷ C, the marked share of the second sample. */
  markedShare: number;
  /** M ÷ the Chapman estimate: the share of the population that carries a mark. */
  populationMarked: number;
  /** True when M + C ≥ the estimate, the condition for Chapman to be unbiased. */
  unbiased: boolean;
  /** Approximate relative standard error, 1 ÷ √R (null when R = 0). */
  relativeError: number | null;
}

export function summarize(
  input: RecaptureInput,
  level: number
): RecaptureSummary {
  const est = chapman(input);
  const variance = chapmanVariance(input);
  return {
    petersen: lincolnPetersen(input),
    chapman: est,
    chapmanVariance: variance,
    chapmanSe: Math.sqrt(variance),
    normal: normalInterval(est, variance, level, distinctSeen(input)),
    exact: exactInterval(input, level),
    seen: distinctSeen(input),
    markedShare: input.recaptured / input.caught,
    populationMarked: input.marked / est,
    unbiased: input.marked + input.caught >= est,
    relativeError:
      input.recaptured === 0 ? null : 1 / Math.sqrt(input.recaptured),
  };
}
